import { useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useWrite } from "../api/hooks";
import type { LocalCredential, LocalEnrolment } from "../api/types";
import { forgetStoredCredential, readStoredCredentials, storeEnrolment, weakPin } from "../auth/localAuth";
import { can, isLocal } from "../auth/roles";
import { ActionDialog } from "../components/ActionDialog";
import { Badge } from "../components/Badges";
import { Notice, ProblemAlert, QueryState, UnknownUnkeyed } from "../components/ProblemAlert";
import { useLocalAuthStatus } from "../components/LocalSignIn";
import { useFireContext } from "../context";
import { formatDateTime, shortId } from "../format/datetime";
import { reasonError, ScreenHeader } from "./shared";
import { Button } from "@lcc/ui";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function Locked({ c }: { c: LocalCredential }) {
  return c.locked ? (
    <Badge tone="warning" label={`Locked after ${c.failed_attempts} wrong PINs${c.locked_at ? ` (${formatDateTime(c.locked_at)})` : ""}`} />
  ) : (
    <Badge tone="neutral" label={c.failed_attempts ? `Usable (${c.failed_attempts} wrong PIN(s))` : "Usable"} />
  );
}

/**
 * Enrol this console for the FER local sign-in: a PIN of 6-12 digits (not one digit, not a straight run) and a label.
 * Only while signed in normally with a second factor (the service refuses otherwise). FER returns the device secret
 * once: it is kept in this browser's storage for this console and never shown or sent anywhere else.
 */
function EnrolForm({ onDone }: { onDone: (stored: boolean) => void }) {
  const { api, principal } = useFireContext();
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [label, setLabel] = useState("Control room console");
  const [err, setErr] = useState<string | null>(null);
  const w = useWrite((a, body: { pin: string; device_label: string }) => endpoints.enrolCredential(a, body));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[0-9]{6,12}$/.test(pin)) return setErr("The PIN is 6 to 12 digits.");
    if (weakPin(pin)) return setErr("Choose a PIN that is not all one digit and not a straight run like 123456.");
    if (pin !== again) return setErr("The two PINs differ.");
    if (!label.trim()) return setErr("Give this console a name (e.g. Console 2).");
    setErr(null);
    w.mutate(
      { pin, device_label: label.trim() },
      {
        onSuccess: (e: LocalEnrolment) => {
          setPin("");
          setAgain("");
          onDone(storeEnrolment(api.base, e, principal));
        },
        onError: () => {
          setPin("");
          setAgain("");
        },
      },
    );
  };
  return (
    <form className="fer-panel fer-form" onSubmit={submit} noValidate aria-label="Enrol this console">
      <h3>Enrol this console for the local sign-in</h3>
      <p>
        If the identity service is ever unreachable, you can then sign in to FER alone on this console with your PIN. Enrol while signed in
        normally with your second factor. At most 3 devices per person.
      </p>
      <label className="fer-field">
        <span>PIN (6 to 12 digits)</span>
        <input type="password" inputMode="numeric" autoComplete="new-password" value={pin} maxLength={12} onChange={(e) => setPin(e.target.value)} />
      </label>
      <label className="fer-field">
        <span>PIN again</span>
        <input type="password" inputMode="numeric" autoComplete="new-password" value={again} maxLength={12} onChange={(e) => setAgain(e.target.value)} />
      </label>
      <label className="fer-field">
        <span>Name of this console</span>
        <input value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} />
      </label>
      {err && (
        <p className="fer-form-error" role="alert">
          {err}
        </p>
      )}
      <ProblemAlert error={w.error} />
      <UnknownUnkeyed show={w.unknown} />
      <div className="fer-actions fer-actions--start">
        <Button type="submit" disabled={w.isPending}>
          Enrol this console
        </Button>
      </div>
    </form>
  );
}

function MyCredentials() {
  const { api } = useFireContext();
  const q = useApi(["my-credentials"], endpoints.listMyCredentials);
  const remove = useWrite((a, id: string) => endpoints.removeMyCredential(a, id));
  const [done, setDone] = useState<null | boolean>(null);
  const here = new Set(readStoredCredentials(api.base).map((c) => c.credential_id));
  const items = q.data?.items ?? [];
  return (
    <section aria-label="Your local credentials">
      <h3>Your local credentials</h3>
      <QueryState isLoading={q.isLoading} error={q.error} label="your credentials" />
      {q.data && items.length === 0 && <p className="fer-muted">None enrolled.</p>}
      {items.length > 0 && (
        <table className="lcc-table" aria-label="Your local credentials">
          <thead>
            <tr>
              <th scope="col">Device</th>
              <th scope="col">Enrolled</th>
              <th scope="col">Last used</th>
              <th scope="col">State</th>
              <th scope="col">Roles last seen</th>
              <th scope="col">
                <span className="lcc-sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.credential_id}>
                <th scope="row">
                  {c.device_label}
                  {here.has(c.credential_id) ? " (this console)" : ""}
                </th>
                <td>{formatDateTime(c.created_at)}</td>
                <td>{formatDateTime(c.last_used_at)}</td>
                <td>
                  <Locked c={c} />
                </td>
                <td>{formatDateTime(c.role_snapshot_at)}</td>
                <td>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(c.credential_id, { onSuccess: () => forgetStoredCredential(api.base, c.credential_id) })}
                    aria-label={`Remove ${c.device_label}`}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ProblemAlert error={remove.error} />
      <UnknownUnkeyed show={remove.unknown} />
      {done === true && (
        <p role="status" className="fer-success">
          <span aria-hidden="true">✓ </span>This console is enrolled. Its device key is kept in this browser only.
        </p>
      )}
      {done === false && (
        <Notice tone="warning">
          Enrolled, but this browser refused to keep the device key, so the credential cannot be used on this console. Remove it and use a
          browser that keeps site data.
        </Notice>
      )}
      <EnrolForm onDone={setDone} />
    </section>
  );
}

function AllCredentials() {
  const { principal } = useFireContext();
  const [locked, setLocked] = useState(false);
  const [reset, setReset] = useState<LocalCredential | null>(null);
  const q = useApi(["credentials", locked], (api) => endpoints.listCredentials(api, { locked: locked || undefined }), { keepPrevious: true });
  const rows = q.data ?? [];
  return (
    <section aria-label="Every local credential">
      <h3>Every local credential</h3>
      <label className="fer-check">
        <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} />
        Locked only
      </label>
      <QueryState isLoading={q.isLoading} error={q.error} label="credentials" />
      {rows.length > 0 && (
        <table className="lcc-table" aria-label={`Local credentials (${rows.length})`}>
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Device</th>
              <th scope="col">Last used</th>
              <th scope="col">State</th>
              <th scope="col">
                <span className="lcc-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.credential_id}>
                <th scope="row">{c.display_name ?? `staff ${shortId(c.party_id)}`}</th>
                <td>{c.device_label}</td>
                <td>{formatDateTime(c.last_used_at)}</td>
                <td>
                  <Locked c={c} />
                </td>
                <td>
                  {c.party_id === principal.partyId ? (
                    <span className="fer-hint">Your own: another FER admin unlocks or revokes it</span>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => setReset(c)} aria-label={`Unlock or revoke the credential of ${c.display_name ?? shortId(c.party_id)} on ${c.device_label}`}>
                      Unlock or revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {reset && (
        <ActionDialog
          title={`Credential of ${reset.display_name ?? shortId(reset.party_id)} on ${reset.device_label}`}
          fields={[
            { name: "action", label: "Action", kind: "select", required: true, options: [["unlock", "Unlock (same PIN and device)"], ["revoke", "Revoke (they enrol again)"]] },
            { name: "reason", label: "Reason (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 },
          ]}
          submitLabel="Apply"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { action: v.action, reason: v.reason.trim() } };
          }}
          write={(api, body) => endpoints.resetCredential(api, reset.credential_id, body as never)}
          onClose={() => setReset(null)}
        />
      )}
    </section>
  );
}

/**
 * During an outage an admin signed in with the FER local sign-in can unlock a colleague's credential (revoking and
 * listing need a normal sign-in; the contract lists no credentials to a local token, so the id is typed).
 */
function UnlockById() {
  const [open, setOpen] = useState(false);
  return (
    <section aria-label="Unlock a credential">
      <h3>Unlock a colleague's credential</h3>
      <p>
        With the FER local sign-in the list of credentials is not available; ask your colleague for the credential id shown on their
        console's sign-in screen.
      </p>
      <Button onClick={() => setOpen(true)}>
        Unlock a credential by its id
      </Button>
      {open && (
        <ActionDialog
          title="Unlock a local credential"
          fields={[
            { name: "credential_id", label: "Credential id", required: true, maxLength: 36 },
            { name: "reason", label: "Reason (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 },
          ]}
          submitLabel="Unlock"
          build={(v) => {
            if (!UUID.test(v.credential_id.trim())) return { error: "The credential id is a UUID (36 characters)." };
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { id: v.credential_id.trim(), action: "unlock", reason: v.reason.trim() } };
          }}
          write={(api, body) => {
            const { id, ...rest } = body as { id: string; action: "unlock"; reason: string };
            return endpoints.resetCredential(api, id, rest);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}

/** The FER local sign-in: the service's state, this person's credentials and enrolment, and (FER admin) every credential. */
export function AccessScreen() {
  const { principal, api, refreshMs } = useFireContext();
  const status = useLocalAuthStatus(api, refreshMs);
  const s = status.data;
  return (
    <div className="fer-screen">
      <ScreenHeader title="Local sign-in" />
      {s && (
        <p role="status">
          {s.local_sign_in_available ? "The identity service is unreachable: the FER local sign-in is available now." : "The identity service answers: the FER local sign-in is not needed now."}{" "}
          <span className="fer-hint">Checked {formatDateTime(s.checked_at)}.</span>
        </p>
      )}
      {can(principal, "viewMyCredentials") && <MyCredentials />}
      {can(principal, "viewAllCredentials") && <AllCredentials />}
      {isLocal(principal) && can(principal, "resetCredential") && <UnlockById />}
    </div>
  );
}
